import assertNever from "assert-never";
import classNames from "classnames";
import { LocalDate } from "@js-joda/core";
import * as React from "react";
import { ReactElement } from "react";
import * as Ons from "react-onsenui";
import { deepEqual } from "../../deep-equal";
import { isLoading, Loading } from "../../Loading";
import * as messages from "../../messages";
import {
  TrainingCenterCaregiverQuestionAnswer,
  TrainingCenterCaregiverVideoBundleItemWatchSegmentParams,
  TrainingCenterDisplayLanguage,
} from "../../messages";
import {
  AgencyId,
  AgencyMemberId,
  TrainingCenterBundleDueDateId,
  TrainingCenterBundleItemId,
  TrainingCenterBundleItemTestQuestionId,
  TrainingCenterBundleItemVideoEngmtQuestionId,
} from "../../messages/ObjectIds";
import {
  TrainingCenterBundleItemDisplayData,
  TrainingCenterCaregiverPDFBundleItemWatchSegmentParams,
  TrainingCenterCaregiverSubmitTestResult,
  TrainingCenterCaregiverVideoWatchData,
  trainingCenterCaregiverVideoWatchDataEmpty,
  TrainingCenterTranslated,
  trainingCenterVideoWatchedPercent,
} from "../../messages/TrainingCenter";
import { STRINGS } from "../../strings";
import { AgencyWithChatOnlyDetails } from "../../system/AppState/AppAgenciesState";
import {
  EngagementChosenAnswer,
  TrainingCenterBundleItem,
  TrainingCenterBundleItemMedia,
  TrainingCenterBundleItemVideoEngagementQuestion,
  TrainingCenterCaregiverBundle,
  TrainingCenterCaregiverTestQuestion,
} from "../../system/AppState/AppStateTrainingCenter";
import { globalize, translate } from "../../translate";
import { HashMap } from "../../utils/HashMap";
import { HashSet } from "../../utils/HashSet";
import { List } from "../../utils/List";
import { comparable, reverseOrd } from "../../utils/OrdTuple";
import { TC_TIMES_SVG } from "../../utils/tc_svgs";
import { VideoRequestAssistance } from "../DocumentOverlayAssistAction";
import { AppBottomSheet } from "../Generic/AppBottomSheet";
import { AppButton } from "../Generic/AppButton";
import { AppIcon } from "../Generic/AppIcon";
import { If } from "../Generic/If";
import PanDown from "../PanDown";
import {
  ChatTypeClicked,
  RequestAssistanceChatBottomSheet,
  SupportOptions,
} from "../RequestAssistanceChatBottomSheet/RequestAssistanceChatBottomSheet";
import { ScreenSkeleton } from "../ScreenSkeleton";
import { simpleAlert } from "../SimpleAlert";
import { VideoInfoBoxDivider } from "../VideoScreen/components/VideoInfoBoxDivider";
import { VideoInfoBoxItem } from "../VideoScreen/components/VideoInfoBoxItem";
import { CallOfficeSheet } from "./CallOfficeSheet";
import TrainingCenterBundleItemFullDescriptionActivity from "./TrainingCenterBundleItemFullDescription/TrainingCenterBundleItemFullDescriptionActivity";
import TrainingCenterBundleItemSummary from "./TrainingCenterBundleItemStatusSummary";
import { TrainingCenterBundleItemSuccess } from "./TrainingCenterBundleItemSuccess";
import {
  FALLBACK_LANGUAGE,
  lookupTranslationOptional,
  lookupTranslationRequired,
  lookupTranslationRequiredWithLanguage,
  nullToUndefined,
  TrainingCenterLanguagePreference,
} from "./TrainingCenterLanguageLookup";
import { TrainingCenterPDFMediaItem } from "./TrainingCenterPDFMediaItem";
import {
  TrainingCenterTestQuestionsList,
  TrainingCenterTestQuestionsListMethods,
} from "./TrainingCenterTestQuestionsList";
import { TrainingCenterTestSubmitPanel } from "./TrainingCenterTestSubmitPanel";
import { getTrainingCenterBundleItemStatus, ItemStatus } from "./TrainingCenterUtils";
import {
  TrainingCenterVideoMediaItem,
  TrainingCenterVideoMediaItemMethods,
} from "./TrainingCenterVideoMediaItem";
import { getOrientationByAspectRatio } from "./TrainingCenterVideoPlayer";
import { useCachedEqual, useFullscreenModeToggle } from "./TrainingCenterVideoPlayer/Utils";

const DEBUG_ENABLED = false as boolean;

export interface TrainingCenterBundleItemScreenProps {
  currentlyDuringShift: boolean;
  agency: messages.Agency | undefined | Loading;
  bundleDueDateId: TrainingCenterBundleDueDateId;
  bundleItemId: TrainingCenterBundleItemId;
  bundleItem: TrainingCenterBundleItem | undefined | Loading;
  languagePreference: TrainingCenterLanguagePreference;
  bundle: TrainingCenterCaregiverBundle | undefined | Loading;
  submitting: boolean;
  allLanguages: List<TrainingCenterDisplayLanguage>;
  agencies: HashMap<AgencyId, AgencyWithChatOnlyDetails> | Loading;
  chosenEngagementAnswers: HashMap<
    TrainingCenterBundleItemVideoEngmtQuestionId,
    EngagementChosenAnswer
  >;
  closedEngagementQuestions: HashSet<TrainingCenterBundleItemVideoEngmtQuestionId>;
  isFullscreen: boolean;
  aspectRatio: number;
  shouldShowNewChat: boolean;
  getOffices: () => Promise<messages.BundleOfficeInfo>;
  onClickEngagementAnswer: (
    question: TrainingCenterBundleItemVideoEngagementQuestion,
    answerValue: string,
    currentSecond: number
  ) => void;
  onClickCloseAnswer: (engagementQuestion: TrainingCenterBundleItemVideoEngagementQuestion) => void;

  onLanguagePreferenceChange: (languagePreference: TrainingCenterLanguagePreference) => void;
  onBackClick: (onBackClick: string) => void;
  onAgencyMemberChatClick: (agencyMemberId: AgencyMemberId, initialMessage: string) => void;
  onQuestionAnswerClick: (
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId,
    trainingCenterBundleItemId: TrainingCenterBundleItemId,
    params: TrainingCenterCaregiverQuestionAnswer
  ) => void;

  onWatchVideoSegment: (
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId,
    trainingCenterBundleItemId: TrainingCenterBundleItemId,
    language: TrainingCenterDisplayLanguage,
    clientUniqueId: number,
    segment: TrainingCenterCaregiverVideoBundleItemWatchSegmentParams
  ) => void;

  onWatchPDFSegment: (
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId,
    trainingCenterBundleItemId: TrainingCenterBundleItemId,
    clientUniqueId: number,
    segment: TrainingCenterCaregiverPDFBundleItemWatchSegmentParams
  ) => void;

  onSubmitTest: (
    bundleDueDateId: TrainingCenterBundleDueDateId,
    bundleItemId: TrainingCenterBundleItemId,
    testVersionNum: number,
    language: TrainingCenterDisplayLanguage,
    caregiverSignatureDataURL: string,
    questionAnswers: TrainingCenterCaregiverQuestionAnswer[]
  ) => void;

  onSubmitTestSuccessConfirm: () => void;
  onChangeFullscreen: (isFullscreen: boolean) => void;
  onChangeVideoAspectRatio: (aspectRatio: number) => void;
  onRequestNavigateToNewChat: () => void;
}

export interface TrainingCenterBundleItemScreenMethods {
  notifyTestSubmissionResult(result: TrainingCenterCaregiverSubmitTestResult): void;
}

export const TrainingCenterBundleItemScreen = React.memo(
  React.forwardRef(function TrainingCenterBundleItemScreen(
    props: TrainingCenterBundleItemScreenProps,
    ref: React.Ref<TrainingCenterBundleItemScreenMethods>
  ): ReactElement {
    const trainingCenterBundleItemPanelRef =
      React.useRef<TrainingCenterBundleItemPanelMethods | null>(null);

    const setFullscreenMode = useFullscreenModeToggle();

    React.useImperativeHandle(
      ref,
      (): TrainingCenterBundleItemScreenMethods => ({
        notifyTestSubmissionResult: (result: TrainingCenterCaregiverSubmitTestResult): void => {
          if (trainingCenterBundleItemPanelRef.current !== null) {
            trainingCenterBundleItemPanelRef.current.notifyTestSubmissionResult(result);
          }
        },
      }),
      []
    );

    const handleChangeFullscreen = React.useCallback(
      (isFullscreen: boolean) => {
        setFullscreenMode(isFullscreen);
        props.onChangeFullscreen.call(null, isFullscreen);
      },
      [props.onChangeFullscreen, setFullscreenMode]
    );

    const videoMediaItemFactory = React.useCallback(
      (vals: {
        ref: React.MutableRefObject<TrainingCenterVideoMediaItemMethods | null>;
        videoInfo: TrainingCenterTranslated<messages.TrainingCenterVideoInfo>;
        media: TrainingCenterBundleItemMedia.Video;
        playing: boolean;
        onSetPlaying: (playing: boolean) => void;
        watchedSegments: [number, number][];
        onWatchSegment: (
          userdata: TrainingCenterDisplayLanguage,
          clientUniqueId: number,
          segment: TrainingCenterCaregiverVideoBundleItemWatchSegmentParams
        ) => void;
      }): JSX.Element => {
        return (
          <TrainingCenterVideoMediaItem<TrainingCenterDisplayLanguage>
            ref={vals.ref}
            userdata={vals.videoInfo.language}
            videoUrl={vals.videoInfo.data.videoUrl}
            videoThumbnailUrl={vals.videoInfo.data.thumbnailUrl}
            videoLengthSeconds={vals.videoInfo.data.videoLengthSeconds}
            videoDimensions={vals.videoInfo.data.videoDimensions}
            videoSubtitles={vals.media.videoSubtitles}
            engagementQuestions={vals.media.engagementQuestions}
            mediaOverlays={vals.media.mediaOverlays}
            languagePreference={props.languagePreference}
            chosenEngagementAnswers={props.chosenEngagementAnswers}
            closedEngagementQuestions={props.closedEngagementQuestions}
            playing={vals.playing}
            isFullscreen={props.isFullscreen}
            aspectRatio={props.aspectRatio}
            onSetPlaying={vals.onSetPlaying}
            onClickEngagementAnswer={props.onClickEngagementAnswer}
            onClickCloseAnswer={props.onClickCloseAnswer}
            watchedSegments={vals.watchedSegments}
            onWatchSegment={vals.onWatchSegment}
            onChangeFullscreen={handleChangeFullscreen}
            onChangeVideoAspectRatio={props.onChangeVideoAspectRatio}
          />
        );
      },
      [
        handleChangeFullscreen,
        props.aspectRatio,
        props.chosenEngagementAnswers,
        props.closedEngagementQuestions,
        props.isFullscreen,
        props.languagePreference,
        props.onChangeVideoAspectRatio,
        props.onClickCloseAnswer,
        props.onClickEngagementAnswer,
      ]
    );

    return (
      <>
        {(() => {
          if (isLoading(props.bundle) || isLoading(props.bundleItem) || isLoading(props.agency)) {
            return (
              <Ons.ProgressCircular
                className="OrientationCenterScreen_loader"
                indeterminate={true}
              />
            );
          }

          if (props.bundle === undefined || props.bundleItem === undefined) {
            return (
              <div>
                ERROR: Bundle Item Not Found: {props.bundleDueDateId}, {props.bundleItemId}
              </div>
            );
          }

          if (!props.bundle.bundleParams.canWatchDuringVisitShift && props.currentlyDuringShift) {
            return (
              <AppBottomSheet
                isOpen={true}
                color="warning"
                rounded={true}
                title={translate(STRINGS.CANT_WATCH_ON_SHIFT)}
                onRequestClose={() => {
                  /* DO NOTHING */
                }}
              >
                <AppButton
                  variant="outline"
                  color="white"
                  text-color="white"
                  onClick={() => props.onBackClick("fade")}
                >
                  {translate(STRINGS.GO_BACK)}
                </AppButton>
              </AppBottomSheet>
            );
          } else {
            return (
              <TrainingCenterBundleItemPanel
                ref={trainingCenterBundleItemPanelRef}
                agency={props.agency}
                bundleDueDateId={props.bundleDueDateId}
                bundleItemId={props.bundleItemId}
                bundleItem={props.bundleItem}
                languagePreference={props.languagePreference}
                bundle={props.bundle}
                submitting={props.submitting}
                agencies={props.agencies}
                videoMediaItemFactory={videoMediaItemFactory}
                getOffices={props.getOffices}
                isFullscreen={props.isFullscreen}
                aspectRatio={props.aspectRatio}
                shouldShowNewChat={props.shouldShowNewChat}
                onAgencyMemberChatClick={props.onAgencyMemberChatClick}
                onQuestionAnswerClick={props.onQuestionAnswerClick}
                onWatchVideoSegment={props.onWatchVideoSegment}
                onWatchPDFSegment={props.onWatchPDFSegment}
                onSubmitTest={props.onSubmitTest}
                onSubmitTestSuccessConfirm={props.onSubmitTestSuccessConfirm}
                onBackClick={props.onBackClick}
                onRequestNavigateToNewChat={props.onRequestNavigateToNewChat}
                onChangeFullscreen={props.onChangeFullscreen}
              />
            );
          }
        })()}
      </>
    );
  })
);

export interface TrainingCenterBundleItemPanelProps {
  agency: messages.Agency | undefined | Loading;
  bundleDueDateId: TrainingCenterBundleDueDateId;
  bundleItemId: TrainingCenterBundleItemId;
  bundleItem: TrainingCenterBundleItem;
  languagePreference: TrainingCenterLanguagePreference;
  bundle: TrainingCenterCaregiverBundle;
  submitting: boolean;
  agencies: HashMap<AgencyId, AgencyWithChatOnlyDetails> | Loading;
  isFullscreen: boolean;
  aspectRatio: number;
  shouldShowNewChat: boolean;

  videoMediaItemFactory: (vals: {
    ref: React.MutableRefObject<TrainingCenterVideoMediaItemMethods | null>;
    videoInfo: TrainingCenterTranslated<messages.TrainingCenterVideoInfo>;
    media: TrainingCenterBundleItemMedia.Video;
    playing: boolean;
    onSetPlaying: (playing: boolean) => void;
    watchedSegments: [number, number][];
    onWatchSegment: (
      userdata: TrainingCenterDisplayLanguage,
      clientUniqueId: number,
      segment: TrainingCenterCaregiverVideoBundleItemWatchSegmentParams
    ) => void;
  }) => JSX.Element;

  getOffices: () => Promise<messages.BundleOfficeInfo>;
  onBackClick: (animation: string) => void;
  onAgencyMemberChatClick: (agencyMemberId: AgencyMemberId, initialMessage: string) => void;
  onQuestionAnswerClick: (
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId,
    trainingCenterBundleItemId: TrainingCenterBundleItemId,
    params: TrainingCenterCaregiverQuestionAnswer
  ) => void;

  onWatchVideoSegment: (
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId,
    trainingCenterBundleItemId: TrainingCenterBundleItemId,
    language: TrainingCenterDisplayLanguage,
    clientUniqueId: number,
    segment: TrainingCenterCaregiverVideoBundleItemWatchSegmentParams
  ) => void;

  onWatchPDFSegment: (
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId,
    trainingCenterBundleItemId: TrainingCenterBundleItemId,
    clientUniqueId: number,
    segment: TrainingCenterCaregiverPDFBundleItemWatchSegmentParams
  ) => void;

  onSubmitTest: (
    bundleDueDateId: TrainingCenterBundleDueDateId,
    bundleItemId: TrainingCenterBundleItemId,
    testVersionNum: number,
    language: TrainingCenterDisplayLanguage,
    caregiverSignatureDataURL: string,
    questionAnswers: TrainingCenterCaregiverQuestionAnswer[]
  ) => void;

  onSubmitTestSuccessConfirm: () => void;
  onRequestNavigateToNewChat: () => void;
  onChangeFullscreen: (isFullscreen: boolean) => void;
}

export interface TrainingCenterBundleItemPanelMethods {
  notifyTestSubmissionResult(result: TrainingCenterCaregiverSubmitTestResult): void;
}

export const TrainingCenterBundleItemPanel = React.memo(
  React.forwardRef(function TrainingCenterBundleItemPanel(
    props: TrainingCenterBundleItemPanelProps,
    ref: React.Ref<TrainingCenterBundleItemPanelMethods>
  ): ReactElement {
    const SUCCESS_LOADING_TIME = 5;
    const mediaRequiredWatchString = getMediaRequiredWatchString(props.bundleItem.media);
    const [showCallOfficeSheet, toggleCallOfficeSheet] = React.useState(false);
    const [showSuccess, setShowSuccess] = React.useState(false);
    const [offices, setOffices] = React.useState<messages.BundleOfficeInfo>();
    const [timeOutAction, setTimeOutAction] = React.useState<NodeJS.Timeout | null>(null);

    // Get offices useEffect
    React.useEffect(() => {
      if (showCallOfficeSheet) {
        const fetchOffices = async () => {
          const officesResp: messages.BundleOfficeInfo = await props.getOffices();
          setOffices(officesResp);
        };
        fetchOffices();
      }
    }, [props, showCallOfficeSheet]);

    const renderCallOfficeSheet = React.useCallback(() => {
      if (offices !== undefined) {
        const itemFailMessageTranslation = lookupTranslationOptional(
          props.languagePreference,
          props.bundleItem.displayData,
          (x) => nullToUndefined(x.testFailMessage)
        );

        const messageDescription =
          itemFailMessageTranslation !== null
            ? itemFailMessageTranslation
            : translate(STRINGS.TEST_FAILED);

        const handleBottomSheetDismiss = () => toggleCallOfficeSheet(false);
        return (
          <CallOfficeSheet
            isOpen={showCallOfficeSheet}
            header={messageDescription}
            description={messageDescription}
            offices={offices.offices}
            agency={props.agency}
            onRequestClose={handleBottomSheetDismiss}
          />
        );
      }
      return "";
    }, [
      offices,
      showCallOfficeSheet,
      props.agency,
      props.bundleItem.displayData,
      props.languagePreference,
    ]);

    React.useImperativeHandle(
      ref,
      (): TrainingCenterBundleItemPanelMethods => ({
        notifyTestSubmissionResult: (result: TrainingCenterCaregiverSubmitTestResult): void => {
          switch (result.type) {
            case "Success":
              if (result.passedTest) {
                switch (props.bundleItem.media.type) {
                  case "Video":
                  case "PDF":
                  case "TestOnly":
                    setShowSuccess(true);
                    setTimeOutAction(
                      setTimeout(() => {
                        setShowSuccess(false);
                        props.onSubmitTestSuccessConfirm();
                      }, SUCCESS_LOADING_TIME * 1000)
                    );
                    break;
                  default:
                    assertNever(props.bundleItem.media);
                }
              } else {
                void simpleAlert({
                  icon: "error",
                  message: translate(STRINGS.TEST_FAILED) + "\nPlease try again.",
                });
              }
              break;
            case "OutdatedTestVersion":
              void simpleAlert({
                icon: "warning",
                message: translate(STRINGS.YOU_MUST_ANSWER_ALL_THE_QUESTIONS),
              });
              break;
            case "AlreadyCompletedBundleItem":
              showTestDisabledAlert(
                props.languagePreference,
                TestDisabledReason.ITEM_COMPLETED,
                mediaRequiredWatchString,
                props.bundleItem.displayData,
                props.agency
              );
              break;
            case "ExemptFromBundleItem":
              showTestDisabledAlert(
                props.languagePreference,
                TestDisabledReason.ITEM_EXEMPT,
                mediaRequiredWatchString,
                props.bundleItem.displayData,
                props.agency
              );
              break;
            case "TestNotUnlocked":
              showTestDisabledAlert(
                props.languagePreference,
                TestDisabledReason.MEDIA_NOT_UNLOCKED,
                mediaRequiredWatchString,
                props.bundleItem.displayData,
                props.agency
              );
              break;
            case "TooManyFailedAttempts":
              showTestDisabledAlert(
                props.languagePreference,
                TestDisabledReason.TOO_MANY_ATTEMPTS,
                mediaRequiredWatchString,
                props.bundleItem.displayData,
                props.agency,
                toggleCallOfficeSheet
              );
              break;
            case "NotFound":
              void simpleAlert({ icon: "error", message: translate(STRINGS.ERROR) });
              break;
            case "BundleNotVisibleToCaregiver":
              void simpleAlert({ icon: "error", message: translate(STRINGS.ERROR) });
              break;
            case "QuestionAnswersInvalid":
              void simpleAlert({
                icon: "warning",
                message: translate(STRINGS.YOU_MUST_ANSWER_ALL_THE_QUESTIONS),
              });
              break;
            default:
              assertNever(result);
          }
        },
      }),
      [mediaRequiredWatchString, props]
    );

    // ---- State ----

    const [playing, setPlaying] = React.useState<boolean>(false);
    const [markedQuestionOverrides, setMarkedQuestionOverrides] = React.useState(
      HashMap.empty<TrainingCenterBundleItemTestQuestionId, number>()
    );
    const [showRequestAssistanceChatBottomSheet, toggleRequestAssistanceChatBottomSheet] =
      React.useState(false);

    // ---- Refs ----

    const trainingCenterTestQuestionsListRef =
      React.useRef<TrainingCenterTestQuestionsListMethods | null>(null);
    const trainingCenterVideoMediaItemRef =
      React.useRef<TrainingCenterVideoMediaItemMethods | null>(null);

    const testDisabledStatus = useCachedEqual(
      React.useMemo(() => {
        return getTestDisabledStaus(props.bundleItem);
      }, [props.bundleItem]),
      deepEqual
    );

    // ---- Callbacks ----

    const handleVideoWatchSegment = React.useCallback(
      (
        userdata: TrainingCenterDisplayLanguage,
        clientUniqueId: number,
        segment: TrainingCenterCaregiverVideoBundleItemWatchSegmentParams
      ) => {
        console.log("VIDEO WATCH_SEGMENT", clientUniqueId, segment);
        props.onWatchVideoSegment.call(
          null,
          props.bundleDueDateId,
          props.bundleItemId,
          userdata,
          clientUniqueId,
          segment
        );
      },
      [props.bundleDueDateId, props.bundleItemId, props.onWatchVideoSegment]
    );

    const handleWatchPDFSegment = React.useCallback(
      (clientUniqueId: number, segment: TrainingCenterCaregiverPDFBundleItemWatchSegmentParams) => {
        console.log("PDF WATCH_SEGMENT", clientUniqueId, segment);
        props.onWatchPDFSegment.call(
          null,
          props.bundleDueDateId,
          props.bundleItemId,
          clientUniqueId,
          segment
        );
      },
      [props.bundleDueDateId, props.bundleItemId, props.onWatchPDFSegment]
    );

    const handlePDFMediaItemLoad = React.useCallback(() => {
      console.log("PDF Loaded");
    }, []);

    // ----

    const showingWrongAnswers =
      props.bundleItem.itemCompleted ||
      (props.bundleItem.testNumAttempts > 0 && props.bundleItem.testParams.showWrongAnswers);

    const preparedAnsweredQuestionsResult = React.useMemo(() => {
      return prepareAnsweredQuestions(
        props.bundleItem.currentTestQuestions,
        markedQuestionOverrides,
        showingWrongAnswers
      );
    }, [markedQuestionOverrides, props.bundleItem.currentTestQuestions, showingWrongAnswers]);

    const allQuestionsMarked = (() => {
      switch (preparedAnsweredQuestionsResult.type) {
        case "PreparedQuestions":
          return true;
        case "MissingQuestionAnswer":
          return false;
        default:
          return assertNever(preparedAnsweredQuestionsResult);
      }
    })();

    const handleQuestionAnswerClick = React.useCallback(
      (bundleItemTestQuestionId: TrainingCenterBundleItemTestQuestionId, index: number) => {
        if (props.submitting) {
          return;
        }

        switch (testDisabledStatus.type) {
          case "TestDisabled":
            showTestDisabledAlert(
              props.languagePreference,
              testDisabledStatus.reason,
              mediaRequiredWatchString,
              props.bundleItem.displayData,
              props.agency,
              toggleCallOfficeSheet
            );
            return;
          case "TestNotDisabled":
            break;
          default:
            assertNever(testDisabledStatus);
        }

        const question = props.bundleItem.currentTestQuestions.find(
          (q) => q.questionId === bundleItemTestQuestionId
        );
        if (question === undefined) {
          // This should never happen
          return;
        }

        // Don't allow the user to change the marking of an answer that is known
        // to be correct
        if (showingWrongAnswers && question.markedAnswer === 0 && question.markedAnswerCorrect) {
          void simpleAlert({
            icon: "warning",
            title: translate(STRINGS.PLEASE_NOTE),
            message: "You can't change an already correct answer", // TODO Translate
          });
          return;
        }

        // Don't allow the user to mark an answer that is known to be
        // incorrect
        if (showingWrongAnswers && question.incorrectlyGuessedAnswers.indexOf(index) >= 0) {
          void simpleAlert({
            icon: "warning",
            title: translate(STRINGS.PLEASE_NOTE),
            message: "You can't select an incorrect answer", // TODO Translate
          });
          return;
        }

        const language = lookupTranslationRequiredWithLanguage(
          props.languagePreference,
          question.displayData,
          (x) => x
        ).language;

        props.onQuestionAnswerClick.call(null, props.bundleDueDateId, props.bundleItemId, {
          questionId: bundleItemTestQuestionId,
          language: language,
          answer: index,
        });
        setMarkedQuestionOverrides((marked) => marked.set(bundleItemTestQuestionId, index));
      },
      [
        mediaRequiredWatchString,
        props.agency,
        props.bundleDueDateId,
        props.bundleItem.currentTestQuestions,
        props.bundleItem.displayData,
        props.bundleItemId,
        props.languagePreference,
        props.onQuestionAnswerClick,
        props.submitting,
        showingWrongAnswers,
        testDisabledStatus,
      ]
    );

    const handleDisabledSubmit = React.useCallback(() => {
      switch (testDisabledStatus.type) {
        case "TestDisabled":
          showTestDisabledAlert(
            props.languagePreference,
            testDisabledStatus.reason,
            mediaRequiredWatchString,
            props.bundleItem.displayData,
            props.agency,
            toggleCallOfficeSheet
          );
          return;
        case "TestNotDisabled":
          break;
        default:
          assertNever(testDisabledStatus);
      }
      switch (preparedAnsweredQuestionsResult.type) {
        case "MissingQuestionAnswer":
          if (trainingCenterTestQuestionsListRef.current !== null) {
            trainingCenterTestQuestionsListRef.current.scrollToQuestion(
              preparedAnsweredQuestionsResult.questionId
            );
          }

          void simpleAlert({
            icon: "warning",
            message: translate(STRINGS.YOU_MUST_ANSWER_ALL_THE_QUESTIONS),
          });
          return;
        case "PreparedQuestions":
          throw new Error(
            `Impossible handleDisabledSubmit when testDisabledStatus is:\n${JSON.stringify(
              testDisabledStatus
            )}\nand preparedAnsweredQuestionsResult is:\n${JSON.stringify(
              preparedAnsweredQuestionsResult
            )}`
          );
        default:
          assertNever(preparedAnsweredQuestionsResult);
      }
    }, [
      mediaRequiredWatchString,
      preparedAnsweredQuestionsResult,
      props.agency,
      props.bundleItem.displayData,
      props.languagePreference,
      testDisabledStatus,
    ]);

    const handleSubmit = React.useCallback(
      (dataURL: string | null) => {
        switch (preparedAnsweredQuestionsResult.type) {
          case "MissingQuestionAnswer":
            throw new Error(
              `Impossible handleSubmit when preparedAnsweredQuestionsResult is:\n${JSON.stringify(
                preparedAnsweredQuestionsResult
              )}`
            );
          case "PreparedQuestions": {
            if (dataURL === null) {
              void simpleAlert({
                icon: "warning",
                message: translate(STRINGS.SUBMIT_YOUR_SIGNATURE),
              });
              return;
            }

            const testLanguage = visibleTestLanguage(
              props.languagePreference,
              props.bundleItem.currentTestQuestions
            );

            props.onSubmitTest.call(
              null,
              props.bundleDueDateId,
              props.bundleItemId,
              props.bundleItem.testParams.versionNum,
              testLanguage,
              dataURL,
              preparedAnsweredQuestionsResult.preparedQuestions
            );
            break;
          }
          default:
            assertNever(preparedAnsweredQuestionsResult);
        }
      },
      [
        preparedAnsweredQuestionsResult,
        props.bundleDueDateId,
        props.bundleItem.currentTestQuestions,
        props.bundleItem.testParams.versionNum,
        props.bundleItemId,
        props.languagePreference,
        props.onSubmitTest,
      ]
    );

    const handleClickVideoBundleChat = () => {
      if (props.shouldShowNewChat) {
        props.onRequestNavigateToNewChat();
        return;
      }

      toggleRequestAssistanceChatBottomSheet(true);
    };

    // ---- Render ----

    const title = lookupTranslationRequired(
      props.languagePreference,
      props.bundleItem.displayData,
      (x) => nullToUndefined(x.title)
    );
    const description = lookupTranslationOptional(
      props.languagePreference,
      props.bundleItem.displayData,
      (x) => nullToUndefined(x.description)
    );

    const itemStatus = getTrainingCenterBundleItemStatus(true, props.bundleItem);

    const itemWatchedTime = getItemWatchedTime(props.languagePreference, props.bundleItem.media);

    const nextVideoTitle = (() => {
      const nextBundleItem = props.bundle.bundleItems.find(
        (i) => i.bundleItemId !== props.bundleItemId && !i.itemCompleted && !i.itemExempt
      );
      if (nextBundleItem === undefined) {
        return "";
      }

      const nextVideoInfo = lookupTranslationRequiredWithLanguage(
        props.languagePreference,
        nextBundleItem.displayData,
        (x) => x
      );
      if (nextVideoInfo.data.title === null) {
        return "";
      }

      return nextVideoInfo.data.title;
    })();

    const testDisabled = (() => {
      switch (testDisabledStatus.type) {
        case "TestDisabled":
          return true;
        case "TestNotDisabled":
          return false;
        default:
          return assertNever(testDisabledStatus);
      }
    })();

    const renderRequestAssistanceChatBottomSheet = () => {
      // Pause video on Chat click
      if (showRequestAssistanceChatBottomSheet && playing) {
        setPlaying(false);
      }
      const handleBottomSheetDismiss = () => toggleRequestAssistanceChatBottomSheet(false);
      const handleChatTypeClick = (supportType: number) => {
        const initialMessage = ((): string => {
          switch (props.bundleItem.media.type) {
            case "PDF":
              return `Hi, i have a question about the PDF "${title}"`;
            case "TestOnly":
              return `Hi, i have a question about the test "${title}"`;
            case "Video":
              if (trainingCenterVideoMediaItemRef.current !== null) {
                const currentSeconds = trainingCenterVideoMediaItemRef.current.getCurrentSeconds();
                return `Hi, i have a question about the video "${title}" at ${formatDuration(
                  currentSeconds
                )}`;
              } else {
                return `Hi, i have a question about the video "${title}"`;
              }
            default:
              return assertNever(props.bundleItem.media);
          }
        })();

        let chatAgencyMemberId: AgencyMemberId | null = null;
        switch (supportType) {
          case ChatTypeClicked.CLINICAL:
            chatAgencyMemberId = props.bundle.bundleParams.chatAgencyMemberId;
            break;
          case ChatTypeClicked.TECHNICAL:
            if (!isLoading(props.agencies)) {
              for (const [, agency] of props.agencies.entries()) {
                if (
                  chatAgencyMemberId === null &&
                  agency.agency.isChatOnly &&
                  agency.agency.defaultAgencyMemberForChat !== null
                ) {
                  chatAgencyMemberId = agency.agency.defaultAgencyMemberForChat;
                }
              }
            }
            break;
          default:
        }
        if (chatAgencyMemberId !== null) {
          props.onAgencyMemberChatClick.call(null, chatAgencyMemberId, initialMessage);
          toggleRequestAssistanceChatBottomSheet(false);
        }
      };

      return (
        <RequestAssistanceChatBottomSheet
          isOpen={showRequestAssistanceChatBottomSheet}
          onClick={handleChatTypeClick}
          onRequestClose={handleBottomSheetDismiss}
          SupportOptions={SupportOptions.TRAINING}
        />
      );
    };

    // DEBUG
    const [showDebugInfo, setShowDebugInfo] = React.useState(false);

    const handleClickClose = React.useCallback(() => {
      if (props.isFullscreen) {
        props.onChangeFullscreen.call(null, false);
        return;
      }

      props.onBackClick.call(null, "slide");
    }, [props.isFullscreen, props.onBackClick, props.onChangeFullscreen]);

    return (
      <PanDown onDragDown={() => props.onBackClick("fade")}>
        <ScreenSkeleton
          title={props.bundleItem.media.type}
          titleBarStyle={ScreenSkeleton.TitleBarStyle.PRIMARY}
          onBackClick={() => props.onBackClick("slide")}
          hideHeader={props.bundleItem.media.type === "Video"}
          rootClassName={"VideoScreen__skeleton TrainingCenterBundleItemScreen__skeleton"}
        >
          <div
            className="lesson-wrapper"
            data-media-type={props.bundleItem.media.type}
            data-video-orientation={getOrientationByAspectRatio(props.aspectRatio)}
            data-video-fullscreen={props.isFullscreen}
          >
            <div className="media-wrapper">
              {(() => {
                switch (props.bundleItem.media.type) {
                  case "Video": {
                    const videoInfo = lookupTranslationRequiredWithLanguage(
                      props.languagePreference,
                      props.bundleItem.media.videoInfo,
                      (x) => x
                    );

                    const watchedSegments = lookupVideoWatchDataForLanguage(
                      props.bundleItem.media,
                      videoInfo.language
                    ).watchedSegments;

                    return (
                      <>
                        <div className="_close" onClick={handleClickClose}>
                          <AppIcon icon={TC_TIMES_SVG} size={20} fill="var(--blue-dark)" />
                        </div>
                        {props.videoMediaItemFactory({
                          ref: trainingCenterVideoMediaItemRef,
                          videoInfo: videoInfo,
                          media: props.bundleItem.media,
                          playing: playing,
                          onSetPlaying: setPlaying,
                          watchedSegments: watchedSegments,
                          onWatchSegment: handleVideoWatchSegment,
                        })}
                      </>
                    );
                  }
                  case "PDF": {
                    const pdfInfo = lookupTranslationRequiredWithLanguage(
                      props.languagePreference,
                      props.bundleItem.media.pdfInfo,
                      (x) => x
                    );

                    const timeLeft =
                      props.bundleItem.itemCompleted || props.bundleItem.itemExempt
                        ? null
                        : Math.max(
                            0,
                            props.bundleItem.media.requiredViewTimeMinutes * 60 -
                              props.bundleItem.media.pdfWatchData.totalSecondsWatched
                          );

                    return (
                      <div className="VideoScreen__mediaWrapper">
                        <TrainingCenterPDFMediaItem
                          language={pdfInfo.language}
                          pdfUrl={pdfInfo.data.pdfUrl}
                          timeLeft={timeLeft}
                          allowPDFDownload={props.bundleItem.media.pdfFileDownloadable}
                          onPDFLoad={handlePDFMediaItemLoad}
                          onWatchSegment={handleWatchPDFSegment}
                        />
                      </div>
                    );
                  }
                  case "TestOnly":
                    return null;
                  default:
                    return assertNever(props.bundleItem.media);
                }
              })()}
            </div>
            <div className="item-content-wrapper">
              <div className="__itemContent">
                <VideoRequestAssistance onClick={handleClickVideoBundleChat} />
                {renderRequestAssistanceChatBottomSheet()}
                <If condition={showCallOfficeSheet}>{renderCallOfficeSheet()}</If>
                <TrainingCenterBundleItemSuccess
                  isOpen={showSuccess}
                  header={
                    props.bundleItem.media.type === "Video"
                      ? translate(STRINGS.VIDEO_COMPLETED_SUCCESSFULLY)
                      : translate(STRINGS.TASK_COMPLETED_SUCCESSFULLY)
                  }
                  description={`${
                    nextVideoTitle !== "" ? nextVideoTitle : translate(STRINGS.NEXT_LESSON)
                  } ${translate(STRINGS.WILL_BEGIN_SHORTLY)}`}
                  buttonText={translate(STRINGS.BEGIN_NOW)}
                  onRequestClose={() => {
                    if (timeOutAction !== null) {
                      clearTimeout(timeOutAction);
                    }
                    setShowSuccess(false);
                    props.onSubmitTestSuccessConfirm.call(null);
                  }}
                  loadingTime={SUCCESS_LOADING_TIME}
                  rounded={true}
                />
                <ItemDetails
                  title={title}
                  description={description}
                  dueDate={props.bundle.completionDate}
                  itemMedia={props.bundleItem.media}
                  watchedAmount={itemWatchedTime}
                  itemStatus={itemStatus}
                  hasTimeLimit={!props.bundle.bundleParams.isOrientation}
                />
                <ItemAttempts
                  maxAttemptsAllowed={props.bundleItem.testParams.testMaxAttemptsAllowed}
                  numAttempts={props.bundleItem.testNumAttempts}
                  itemCompleted={props.bundleItem.itemCompleted}
                  questionsLength={props.bundleItem.currentTestQuestions.length()}
                  correctAnswersLength={props.bundleItem.currentTestQuestions
                    .filter((q) => q.markedAnswerCorrect)
                    .length()}
                />
                {DEBUG_ENABLED ? (
                  <div style={{ textAlign: "right" }}>
                    <span onClick={() => setShowDebugInfo(!showDebugInfo)}>DEBUG</span>
                  </div>
                ) : null}
                {showDebugInfo ? (
                  <pre>
                    bundleDueDateId: {props.bundleDueDateId}
                    {"\n"}
                    bundleItemId: {props.bundleItemId}
                    {"\n"}
                    numAttempts: {props.bundleItem.testNumAttempts}
                    {"\n"}
                    testUnlocked: {`${props.bundleItem.testUnlocked}`}
                    {"\n"}
                    itemCompleted: {`${props.bundleItem.itemCompleted}`}
                    {"\n"}
                    testDisabledStatus: {JSON.stringify(testDisabledStatus)}
                    {"\n"}
                    {JSON.stringify(props.bundleItem.testParams, null, 2)}
                  </pre>
                ) : null}
                <TrainingCenterBundleItemFullDescriptionActivity
                  fullDescriptions={props.bundleItem.fullDescriptions}
                  languagePreference={props.languagePreference}
                />
                <TrainingCenterTestQuestionsList
                  ref={trainingCenterTestQuestionsListRef}
                  disabled={props.submitting || testDisabled}
                  testCompleted={props.bundleItem.itemCompleted}
                  showWrongAnswers={props.bundleItem.testParams.showWrongAnswers}
                  languagePreference={props.languagePreference}
                  questions={props.bundleItem.currentTestQuestions}
                  markedQuestionOverride={markedQuestionOverrides}
                  onQuestionAnswerClick={handleQuestionAnswerClick}
                />
                <TrainingCenterTestSubmitPanel
                  key={`${props.bundleDueDateId}-${props.bundleItemId}-${props.bundleItem.testNumAttempts}`} // This is a hack to clear the signature after every attempt
                  disabled={testDisabled || !allQuestionsMarked}
                  submitting={props.submitting}
                  initialSignatureDataURL={props.bundleItem.caregiverSignatureDataURL}
                  onDisabledClick={handleDisabledSubmit}
                  onSubmit={handleSubmit}
                />
              </div>
            </div>
          </div>
        </ScreenSkeleton>
      </PanDown>
    );
  })
);

function getItemWatchedTime(
  language: TrainingCenterLanguagePreference,
  media: TrainingCenterBundleItemMedia
): string {
  switch (media.type) {
    case "Video": {
      const videoInfo = lookupTranslationRequiredWithLanguage(language, media.videoInfo, (x) => x);

      const watchData = lookupVideoWatchDataForLanguage(media, videoInfo.language);
      const duration = formatDuration(watchData.totalSecondsWatched);
      const percentWatched = trainingCenterVideoWatchedPercent(videoInfo.data, watchData);
      return `${duration} (${percentWatched}%)`;
    }
    case "PDF": {
      const duration = formatDuration(media.pdfWatchData.totalSecondsWatched);
      const percentWatched =
        media.requiredViewTimeMinutes === 0
          ? 100
          : Math.floor(
              (100 * media.pdfWatchData.totalSecondsWatched) / (media.requiredViewTimeMinutes * 60)
            );
      return `${duration} (${Math.min(100, percentWatched)}%)`;
    }
    case "TestOnly":
      return translate(STRINGS.NA);
    default:
      return assertNever(media);
  }
}

enum TestDisabledReason {
  ITEM_COMPLETED,
  ITEM_EXEMPT,
  MEDIA_NOT_UNLOCKED,
  TOO_MANY_ATTEMPTS,
}

type TestDisabledStatus = TestDisabledStatus.TestNotDisabled | TestDisabledStatus.TestDisabled;

declare namespace TestDisabledStatus {
  export interface TestNotDisabled {
    type: "TestNotDisabled";
  }

  export interface TestDisabled {
    type: "TestDisabled";
    reason: TestDisabledReason;
  }
}

function getTestDisabledStaus(bundleItem: TrainingCenterBundleItem): TestDisabledStatus {
  if (bundleItem.itemCompleted) {
    return {
      type: "TestDisabled",
      reason: TestDisabledReason.ITEM_COMPLETED,
    };
  }

  if (bundleItem.itemExempt) {
    return {
      type: "TestDisabled",
      reason: TestDisabledReason.ITEM_EXEMPT,
    };
  }

  if (!bundleItem.testUnlocked) {
    return {
      type: "TestDisabled",
      reason: TestDisabledReason.MEDIA_NOT_UNLOCKED,
    };
  }

  if (bundleItem.testNumAttempts >= bundleItem.testParams.testMaxAttemptsAllowed) {
    return {
      type: "TestDisabled",
      reason: TestDisabledReason.TOO_MANY_ATTEMPTS,
    };
  }

  return {
    type: "TestNotDisabled",
  };
}

function getMediaRequiredWatchString(bundleItemMedia: TrainingCenterBundleItemMedia): string {
  switch (bundleItemMedia.type) {
    case "Video":
      return bundleItemMedia.requiredWatchTimePercent + "%";
    case "PDF":
      return globalize().formatUnit(bundleItemMedia.requiredViewTimeMinutes, "minute", {
        form: "short",
      });
    case "TestOnly":
      return translate(STRINGS.NA);
    default:
      return assertNever(bundleItemMedia);
  }
}

function showTestDisabledAlert(
  _languagePreference: TrainingCenterLanguagePreference,
  testDisabledReason: TestDisabledReason,
  mediaRequiredWatchString: string,
  _bundleItemDisplayData: List<TrainingCenterTranslated<TrainingCenterBundleItemDisplayData>>,
  _agency: messages.Agency | undefined | Loading,
  toggleCallOfficeSheet?: (state: boolean) => void
): void {
  switch (testDisabledReason) {
    case TestDisabledReason.ITEM_COMPLETED:
      void simpleAlert({
        icon: "warning",
        title: translate(STRINGS.PLEASE_NOTE),
        message:
          translate(STRINGS.YOU_MAY_NOT_ANSWER_THE_TEST_BECAUSE) +
          " " +
          translate(STRINGS.YOU_ALREADY_SUCCEEDED),
      });
      break;
    case TestDisabledReason.ITEM_EXEMPT:
      void simpleAlert({
        icon: "warning",
        title: translate(STRINGS.PLEASE_NOTE),
        message:
          translate(STRINGS.YOU_MAY_NOT_ANSWER_THE_TEST_BECAUSE) +
          " " +
          translate(STRINGS.YOU_ARE_EXEMPT_FROM_THIS_TEST),
      });
      break;
    case TestDisabledReason.MEDIA_NOT_UNLOCKED:
      void simpleAlert({
        icon: "warning",
        title: translate(STRINGS.PLEASE_NOTE),
        message:
          translate(STRINGS.YOU_MAY_NOT_ANSWER_THE_TEST_BECAUSE) +
          " " +
          translate(STRINGS.YOU_MAY_ONLY_SUBMIT_AFTER) +
          " " +
          mediaRequiredWatchString +
          " " +
          translate(STRINGS.OF_THE_REQUIRED_PLEASE_CONTINUE),
      });
      break;
    case TestDisabledReason.TOO_MANY_ATTEMPTS: {
      if (toggleCallOfficeSheet !== undefined) {
        toggleCallOfficeSheet(true);
      }
      break;
    }
    default:
      assertNever(testDisabledReason);
  }
}

type PrepareAnsweredQuestionsResult =
  | PrepareAnsweredQuestionsResult.PreparedQuestions
  | PrepareAnsweredQuestionsResult.MissingQuestionAnswer;

declare namespace PrepareAnsweredQuestionsResult {
  export interface PreparedQuestions {
    type: "PreparedQuestions";
    preparedQuestions: TrainingCenterCaregiverQuestionAnswer[];
  }

  export interface MissingQuestionAnswer {
    type: "MissingQuestionAnswer";
    questionId: TrainingCenterBundleItemTestQuestionId;
  }
}

function prepareAnsweredQuestions(
  currentTestQuestions: List<TrainingCenterCaregiverTestQuestion>,
  markedQuestionOverrides: HashMap<TrainingCenterBundleItemTestQuestionId, number>,
  showingWrongAnswers: boolean
): PrepareAnsweredQuestionsResult {
  const preparedQuestions: TrainingCenterCaregiverQuestionAnswer[] = [];
  for (const question of currentTestQuestions) {
    const answerOverride = markedQuestionOverrides.get(question.questionId);

    const answer = answerOverride !== undefined ? answerOverride : question.markedAnswer;

    if (
      answer === null ||
      (showingWrongAnswers && question.incorrectlyGuessedAnswers.indexOf(answer) >= 0)
    ) {
      return {
        type: "MissingQuestionAnswer",
        questionId: question.questionId,
      };
    }

    preparedQuestions.push({
      questionId: question.questionId,
      answer: answer,
    });
  }
  return {
    type: "PreparedQuestions",
    preparedQuestions: preparedQuestions,
  };
}

function visibleTestLanguage(
  languagePreference: TrainingCenterLanguagePreference,
  testQuestions: List<TrainingCenterCaregiverTestQuestion>
): TrainingCenterDisplayLanguage {
  // The way to most accurately determine what language the test is in, is
  // to look at the questions and see in what language they actually appear
  // (according to the user's languagePreference).
  //
  // It's possible that not all of the questions are translated, so we see
  // what language the majority of the test questions appear in

  const languagesCounter = new Map<TrainingCenterDisplayLanguage, number>();
  for (const question of testQuestions) {
    const language = lookupTranslationRequiredWithLanguage(
      languagePreference,
      question.displayData,
      (x) => x
    ).language;
    const count = languagesCounter.get(language);
    if (count === undefined) {
      languagesCounter.set(language, 1);
    } else {
      languagesCounter.set(language, count + 1);
    }
  }
  const majorityLanguage = List.from(languagesCounter.entries())
    .sortBy(([language, count]) =>
      comparable(reverseOrd(count), TrainingCenterDisplayLanguage.unwrap(language))
    )
    .first();

  if (majorityLanguage !== undefined) {
    const [language] = majorityLanguage;
    return language;
  } else {
    // This happens when the test has no questions.
    return languagePreference.preferredLanguage !== null
      ? languagePreference.preferredLanguage
      : FALLBACK_LANGUAGE;
  }
}

interface ItemDetailsProps {
  title: string;
  description: string | null;
  dueDate: LocalDate;
  itemMedia: TrainingCenterBundleItemMedia;
  watchedAmount: string;
  itemStatus: ItemStatus;
  hasTimeLimit: boolean;
}

const ItemDetails = React.memo(function ItemDetails(props: ItemDetailsProps): ReactElement {
  const submitByValue = () => {
    if (props.hasTimeLimit) {
      return globalize().formatLocalDate(props.dueDate, { date: "medium" });
    }

    return translate(STRINGS.NO_TIME_LIMIT);
  };

  return (
    <div className="VideoScreen__details">
      <div className="VideoScreen__infoBox">
        <VideoInfoBoxItem label={translate(STRINGS.SUBMIT_BY)} value={submitByValue()} />

        <VideoInfoBoxDivider />
        <VideoInfoBoxItem label={translate(STRINGS.WATCHED)} value={props.watchedAmount} />
        <VideoInfoBoxDivider />
        <TrainingCenterBundleItemSummary
          itemMedia={props.itemMedia}
          itemStatus={props.itemStatus}
        />
      </div>
      <div className="VideoScreen__description">
        <h2>{props.title}</h2>
        <p>{props.description !== null ? props.description : null}</p>
      </div>
    </div>
  );
});

interface ItemAttemptsProps {
  maxAttemptsAllowed: number;
  numAttempts: number;
  itemCompleted: boolean;
  questionsLength: number;
  correctAnswersLength: number;
}

const ItemAttempts = React.memo(function ItemAttempts(props: ItemAttemptsProps): ReactElement {
  const attemptsLeft = React.useMemo(() => props.maxAttemptsAllowed - props.numAttempts, [props]);

  const attemptsText = React.useMemo(() => {
    if (props.itemCompleted) {
      return translate(STRINGS.YOU_HAVE_COMPLETED_THIS_TEST);
    }

    if (attemptsLeft === 0) {
      return translate(STRINGS.YOU_HAVE_NO_ATTEMPTS_LEFT_PLEASE_CONTACT_YOUR_AGENCY);
    }

    if (attemptsLeft === 1) {
      return translate(STRINGS.YOU_HAVE_ONE_ATTEMPT_LEFT);
    }

    if (attemptsLeft === props.maxAttemptsAllowed) {
      return translate(STRINGS.YOU_HAVE_X_ATTEMPTS_TO_ANSWER_THIS_TEST).replace(
        "%d",
        props.maxAttemptsAllowed.toString()
      );
    }

    return translate(STRINGS.YOU_HAVE_X_ATTEMPTS_LEFT).replace("%d", attemptsLeft.toString());
  }, [attemptsLeft, props]);

  const completionText = React.useMemo(() => {
    if (props.correctAnswersLength === props.questionsLength) {
      return translate(STRINGS.TEST_COMPLETED);
    }

    return translate(STRINGS.YOU_HAVE_X_CORRECT_ANSWERS_OUT_OF_Y)
      .replace("%d", props.correctAnswersLength.toString())
      .replace("%d", props.questionsLength.toString());
  }, [props]);

  const testPercents = React.useMemo(() => {
    return ((props.correctAnswersLength / props.questionsLength) * 100).toFixed(0) + "%";
  }, [props]);

  if (props.questionsLength === 0) {
    return <></>;
  }

  return (
    <div
      className={classNames("ItemAttempts", {
        __noAttempts: attemptsLeft === 0,
        __completed: props.itemCompleted,
      })}
    >
      <div className="__text">{attemptsText}.</div>
      <If condition={testPercents !== "0%"}>
        <div className="__progressBar">
          <div style={{ width: testPercents }} />
        </div>
        <div className="__progressText">{completionText}</div>
      </If>
    </div>
  );
});

function lookupVideoWatchDataForLanguage(
  video: TrainingCenterBundleItemMedia.Video,
  language: TrainingCenterDisplayLanguage
): TrainingCenterCaregiverVideoWatchData {
  const result = video.videoWatchData.find((w) => w.language === language);
  if (result !== undefined) {
    return result.data;
  } else {
    return trainingCenterCaregiverVideoWatchDataEmpty;
  }
}

export function formatDuration(durationSeconds: number): string {
  let hours: string | number = Math.floor(durationSeconds / 3600);
  let minutes: string | number = Math.floor((durationSeconds - hours * 3600) / 60);
  let seconds: string | number = durationSeconds - hours * 3600 - minutes * 60;

  if (hours < 10) {
    if (hours === 0) {
      hours = "";
    } else {
      hours = "0" + hours + ":";
    }
  }
  if (minutes < 10) {
    minutes = "0" + minutes;
  }
  if (seconds < 10) {
    seconds = "0" + seconds;
  }
  return "" + hours + minutes + ":" + seconds;
}
